import * as React from 'react';
import { logError } from '../utils/telemetry';

/**
 * PROMISSORIA RMX: Tipagem estrita para Error Boundary.
 * Resolve o erro TS2339 (state/props not found) através de herança explícita.
 */
interface ErrorBoundaryProps {
  children?: React.ReactNode;
  fallback?: React.ReactNode;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  // Inicialização com tipagem forçada para o compilador
  public state: ErrorBoundaryState = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('R-MAX Runtime Error:', error, errorInfo);
    
    // Registrar erro via telemetria no Supabase
    logError(error, 'ReactErrorBoundary', {
      componentStack: errorInfo.componentStack
    });
    
    // Verificação de erro de carregamento de chunk (comum após novo deploy onde hashes antigos somem)
    const isChunkError = 
      error.name === 'ChunkLoadError' || 
      /Failed to fetch dynamically imported module/i.test(error.message) ||
      /Loading chunk .* failed/i.test(error.message);

    if (isChunkError) {
      const reloadKey = 'rmax_chunk_reload_attempted';
      const hasAttemptedReload = sessionStorage.getItem(reloadKey);

      if (!hasAttemptedReload) {
        // Marca que tentamos recarregar para evitar loop infinito se o erro persistir
        sessionStorage.setItem(reloadKey, 'true');
        console.warn('R-MAX: Erro de carregamento de módulo detectado. Forçando atualização da aplicação...');
        
        // Pequeno atraso para garantir que o log seja visto (opcional) e evitar travamento imediato
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        console.error('R-MAX: Uma tentativa de recarregamento automático já foi feita e o erro persiste.');
      }
    }
  }

  public resetBoundary = (): void => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  public render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      
      const handleClearAndReload = () => {
        try {
          // Preserva credenciais de autenticação para nunca deslogar o assinante
          const authKeys: Record<string, string> = {};
          for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k && (k.startsWith('sb-') || k.includes('auth-token') || k.includes('supabase'))) {
              authKeys[k] = localStorage.getItem(k) || '';
            }
          }
          localStorage.clear();
          sessionStorage.clear();
          Object.entries(authKeys).forEach(([k, v]) => localStorage.setItem(k, v));
        } catch (e) {
          console.error(e);
        }
        window.location.reload();
      };

      return (
        <div style={{ 
          padding: '40px 24px', 
          textAlign: 'center', 
          backgroundColor: '#0a0a0a', 
          color: '#ffffff', 
          borderRadius: '24px', 
          margin: '24px auto', 
          maxWidth: '480px',
          border: '1px solid #27272a',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#ef4444',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 0 20px rgba(239, 68, 68, 0.4)'
          }}>
            <svg style={{ width: '32px', height: '32px', color: '#ffffff' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 style={{ fontSize: '20px', fontWeight: '900', marginBottom: '12px', color: '#f4f4f5' }}>Ops, encontramos um pequeno problema na memória</h2>
          <p style={{ fontSize: '14px', marginBottom: '32px', color: '#a1a1aa', lineHeight: '1.6' }}>
            Dados corrompidos ou inválidos no cache podem ter causado este travamento temporário da tela. Vamos limpar os dados locais para restaurar o sistema.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button 
              type="button" 
              onClick={this.resetBoundary}
              style={{ 
                backgroundColor: 'transparent', 
                color: '#e4e4e7', 
                border: '1px solid #3f3f46', 
                padding: '12px 24px', 
                borderRadius: '12px', 
                fontWeight: 'bold', 
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
            >
              Tentar Novamente
            </button>
            <button 
              type="button" 
              onClick={handleClearAndReload}
              style={{ 
                backgroundColor: '#dc2626', 
                color: 'white', 
                border: 'none', 
                padding: '12px 24px', 
                borderRadius: '12px', 
                fontWeight: 'bold', 
                cursor: 'pointer',
                fontSize: '14px',
                boxShadow: '0 4px 6px -1px rgba(220, 38, 38, 0.2)',
                transition: 'all 0.2s'
              }}
            >
              Limpar e Recarregar
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
